/**
 * FearGauge Component — Compact single-line layout
 *
 * VIX value + horizontal zone bar + status pill, all on one line.
 * Zones: 0-15 QUIET → 15-25 OPTIMAL → 25-30 ELEVATED → 30+ EXTREME.
 * Trader theme: neutral grey track with zone ticks; only ELEVATED/EXTREME
 * (a warning) take colour, in amber.
 */

import React from 'react';
import InfoTooltip from '../../common/InfoTooltip';

interface FearGaugeProps {
  vix: number;
  loading?: boolean;
}

const VIX_MIN = 8;
const VIX_MAX = 45;

const ZONES = [
  { min: 0,  max: 15, label: 'Quiet' },
  { min: 15, max: 25, label: 'Optimal' },
  { min: 25, max: 30, label: 'Elevated' },
  { min: 30, max: 45, label: 'Extreme' },
];

// Axis labels: Elevated + Extreme read as one "High" zone under the bar.
const LABELS = [
  { min: 0,  max: 15, label: 'Quiet' },
  { min: 15, max: 25, label: 'Optimal' },
  { min: 25, max: 45, label: 'High' },
];

const getZone = (vix: number) => {
  if (vix >= 30) return { warn: true, label: 'EXTREME' };
  if (vix >= 25) return { warn: true, label: 'ELEVATED' };
  if (vix >= 15) return { warn: false, label: 'OPTIMAL' };
  return { warn: false, label: 'QUIET' };
};

const TOOLTIP = 'The VIX measures market fear. Higher VIX means higher option premiums — better for selling covered calls. The 15-25 zone is optimal.';

const FearGauge: React.FC<FearGaugeProps> = ({ vix, loading }) => {
  const zone = getZone(vix);

  if (loading) {
    return (
      <div className="market-context-card fear-gauge-card fear-gauge-inline">
        <div className="market-context-card-header">
          <div className="market-context-card-title">
            Fear Gauge
            <InfoTooltip text={TOOLTIP} />
          </div>
        </div>
        <div className="market-context-loading">Loading...</div>
      </div>
    );
  }

  const range = VIX_MAX - VIX_MIN;
  const clampedVix = Math.max(VIX_MIN, Math.min(VIX_MAX, vix));
  const markerPct = ((clampedVix - VIX_MIN) / range) * 100;

  return (
    <div className="market-context-card fear-gauge-card fear-gauge-inline">
      <div className="market-context-card-header" style={{ marginBottom: 0 }}>
        <div className="market-context-card-title">
          Fear Gauge
          <InfoTooltip text={TOOLTIP} />
        </div>
        <div className={`market-context-status-pill${zone.warn ? ' is-warn' : ''}`}>
          {zone.label}
        </div>
      </div>

      {/* Inline row: VIX value + zone bar */}
      <div className="fear-gauge-inline-row">
        <span className={`fear-gauge-inline-value${zone.warn ? ' is-warn' : ''}`}>
          {vix.toFixed(1)}
          <span className="fear-gauge-inline-unit">VIX</span>
        </span>

        {/* Zone bar */}
        <div className="fear-gauge-inline-bar">
          <div className="fear-gauge-bar-track">
            {ZONES.map((z) => {
              const zoneStart = Math.max(z.min, VIX_MIN);
              const zoneEnd = Math.min(z.max, VIX_MAX);
              const widthPct = ((zoneEnd - zoneStart) / range) * 100;
              return (
                <div
                  key={z.label}
                  className={`fear-gauge-zone fear-gauge-zone-${z.label.toLowerCase()}`}
                  style={{ width: `${widthPct}%` }}
                />
              );
            })}
            {/* Position marker */}
            <div
              className="fear-gauge-marker"
              style={{ left: `${markerPct}%` }}
            />
          </div>
          {/* Each label sits at the centre of its zone on the same scale as the
              bar (zones are unequal: Quiet 8-15, Optimal 15-25, High 25-45). */}
          <div className="fear-gauge-bar-labels">
            {LABELS.map((l) => {
              const centre = ((Math.max(l.min, VIX_MIN) + Math.min(l.max, VIX_MAX)) / 2 - VIX_MIN) / range * 100;
              return <span key={l.label} style={{ left: `${centre}%` }}>{l.label}</span>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FearGauge;
